import {
  ActionIcon,
  Anchor,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconPlayerStop, IconRefresh, IconExternalLink } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { UploadUtils } from '../UploadUtils';
import { UploadProgressBar } from './UploadProgressBar';
import type { UploadItem } from '@/lib/types/Upload';
import type { UploadItemStatus } from '@/lib/constants/ingest';

interface UploadFileItemProps {
  item: UploadItem;
  onCancel: (uppyFileId: string) => void;
  onRetry: (uppyFileId: string) => void;
}

const STATUS_BADGE: Record<UploadItemStatus, { color: string; label: string }> = {
  pending: { color: 'gray', label: 'Pending' },
  creating: { color: 'blue', label: 'Creating' },
  uploading: { color: 'blue', label: 'Uploading' },
  processing: { color: 'orange', label: 'Processing' },
  ready: { color: 'green', label: 'Ready' },
  error: { color: 'red', label: 'Error' },
  cancelled: { color: 'gray', label: 'Cancelled' },
};

function formatSpeed(bytesUploaded: number, startedAt: number | null): string | null {
  if (!startedAt) return null;
  const elapsedSecs = (Date.now() - startedAt) / 1000;
  if (elapsedSecs < 1) return null;
  const bytesPerSec = bytesUploaded / elapsedSecs;
  if (bytesPerSec >= 1024 * 1024) {
    return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
  }
  return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
}

export function UploadFileItem({ item, onCancel, onRetry }: UploadFileItemProps) {
  const navigate = useNavigate();
  const badge = STATUS_BADGE[item.status];
  const canCancel = item.status === 'pending' || item.status === 'creating' || item.status === 'uploading';
  const canRetry = item.status === 'error' && item.uppyFileId;
  const showLink = item.mediaId > 0 && item.status === 'ready';
  const speed = item.status === 'uploading' ? formatSpeed(item.bytesUploaded, item.startedAt) : null;

  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {item.filename}
            </Text>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {UploadUtils.formatFileSize(item.fileSize)}
              </Text>
              {speed && (
                <Text size="xs" c="dimmed">
                  {speed}
                </Text>
              )}
              <Badge size="xs" color={badge.color} variant="light">
                {badge.label}
              </Badge>
            </Group>
          </Stack>

          <Group gap={4} wrap="nowrap">
            {canCancel && (
              <Tooltip label="Cancel upload">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={() => onCancel(item.uppyFileId)}
                >
                  <IconPlayerStop size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            {canRetry && (
              <Tooltip label="Retry upload">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={() => onRetry(item.uppyFileId)}
                >
                  <IconRefresh size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            {showLink && (
              <Tooltip label="Go to video">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={() =>
                    navigate({ to: '/videos/$id', params: { id: bcLong.getStr(item.mediaId) } })
                  }
                >
                  <IconExternalLink size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        <UploadProgressBar
          status={item.status}
          uploadProgress={item.uploadProgress}
          processingProgress={item.processingProgress}
        />

        {item.error && (
          <Text size="xs" c="red">
            {item.error}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
